/**
 * useMonthEndProximity — F7: Month-End Proximity Utility Hook
 *
 * Returns the proximity of today's date to the company's financial period-end
 * date, along with a resolved urgency level and banner text. Used by Finance
 * dashboard widgets to apply conditional amber/red border + banner styling
 * as defined in PRD §2.9.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️  ENGINEERING NOTE — Period-End Date Source
 * ─────────────────────────────────────────────────────────────────────────────
 * The period-end date MUST come from the connected finance system (Access
 * Financials / Access Dimensions), not be assumed from the calendar.
 *
 * Reasons:
 *   - Companies may run 4-4-5, 4-5-4, or non-calendar financial years
 *   - Some close on a fixed day-of-week (e.g. last Friday) not the last calendar day
 *   - Year-end months vary (March, June, September year-ends are all common)
 *
 * What the engineering team needs to do:
 *   1. Identify the Access Financials / Access Dimensions API endpoint that
 *      returns the current open period's end date for a given database/company.
 *      Likely candidates: `/api/periods/current`, `/api/accounting-periods`,
 *      or similar — check the Financials API documentation.
 *   2. Add a `currentPeriodEndDate: string | null` field to the
 *      `CompanyWithIntegrations` type (ISO 8601 date string, e.g. "2025-06-30").
 *   3. Populate this field when fetching the company in `integrationsRedesignService`
 *      by calling the Financials API alongside the integration status fetch.
 *   4. Pass the resolved date into `getMonthEndProximity(today, periodEndDate)`
 *      (the pure utility exported below) — it already accepts it as a parameter.
 *
 * CURRENT MOCK BEHAVIOUR:
 *   Until the above is implemented, `fetchFinancePeriodEndDate()` returns null.
 *   The `resolveEffectivePeriodEnd()` fallback then uses the last calendar day
 *   of the current month. This is a safe approximation for demo purposes only.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Usage:
 *   const { urgency, bannerText, daysToClose, daysOverdue, periodEndDate } =
 *     useMonthEndProximity();
 *
 *   // Widget border class
 *   const borderClass =
 *     urgency === 'red'   ? 'border-red-500' :
 *     urgency === 'amber' ? 'border-amber-400' : '';
 */

import { useState, useEffect } from 'react';
import { useCompany } from '@/contexts/CompanyContext';

// ── PRD §2.9 urgency levels ───────────────────────────────────────────────────

export type MonthEndUrgency =
  /** > 5 business days to close — no special treatment */
  | 'normal'
  /** ≤ 5 business days to close — amber border + banner */
  | 'amber'
  /** Close due today, or 1–2 days overdue — red border + banner */
  | 'red';

// ── Return shape ──────────────────────────────────────────────────────────────

export interface MonthEndProximityResult {
  /**
   * Calendar days remaining until the period-end date (0 = today is close day).
   * Null while loading or when finance is not connected.
   */
  daysToClose: number | null;
  /**
   * Calendar days since the period-end date passed (1 = one day overdue).
   * Null if not yet overdue or finance not connected.
   */
  daysOverdue: number | null;
  /** Resolved urgency level per PRD §2.9. 'normal' when not connected. */
  urgency: MonthEndUrgency;
  /**
   * Ready-to-render banner string per PRD §2.9, or null when urgency is 'normal'.
   * Examples:
   *   "Month-end close in 3 days"
   *   "Close due today"
   *   "Month-end overdue"
   */
  bannerText: string | null;
  /**
   * The resolved period-end date used for calculations (ISO 8601 date string).
   * When sourced from the finance system this will be the actual period-end.
   * When falling back to mock it will be the last calendar day of the current month.
   * Null while loading or when finance is not connected.
   */
  periodEndDate: string | null;
  /** True while the period-end date is being fetched. */
  isLoading: boolean;
}

// ── Pure utility — usable outside React ──────────────────────────────────────

/**
 * Given today's date and a period-end date, returns the full proximity result.
 *
 * This is the core calculation function. It is deliberately a pure function
 * (no side effects, no hooks) so it can be unit-tested in isolation and reused
 * in non-React contexts (e.g. server-side rendering, export utilities).
 *
 * Business-day counting for the ≤5 day amber threshold uses calendar days for
 * simplicity in this iteration. A future improvement could swap in a proper
 * business-day library (e.g. date-fns `differenceInBusinessDays`) once holiday
 * calendars are available.
 *
 * @param today        - The current date (injected for testability)
 * @param periodEndDate - ISO 8601 date string of the period-end, e.g. "2025-06-30"
 */
export function getMonthEndProximity(
  today: Date,
  periodEndDate: string,
): MonthEndProximityResult {
  // Normalise both dates to midnight UTC to avoid time-of-day drift
  const todayMidnight = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const periodEnd = new Date(periodEndDate + 'T00:00:00Z');

  const diffMs = periodEnd.getTime() - todayMidnight.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  // diffDays > 0  → days remaining
  // diffDays === 0 → today is the close day
  // diffDays < 0  → overdue by |diffDays| days

  let urgency: MonthEndUrgency;
  let bannerText: string | null;
  let daysToClose: number | null = null;
  let daysOverdue: number | null = null;

  if (diffDays > 5) {
    // Normal — more than 5 days away
    urgency = 'normal';
    bannerText = null;
    daysToClose = diffDays;
  } else if (diffDays > 0) {
    // Amber — ≤ 5 business days to close
    urgency = 'amber';
    daysToClose = diffDays;
    bannerText = `Month-end close in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  } else if (diffDays === 0) {
    // Red — close due today
    urgency = 'red';
    bannerText = 'Close due today';
  } else if (diffDays >= -2) {
    // Red — 1–2 days overdue
    urgency = 'red';
    daysOverdue = Math.abs(diffDays);
    bannerText = 'Month-end overdue';
  } else {
    // More than 2 days overdue — treat as normal (period closed, next period open)
    urgency = 'normal';
    bannerText = null;
    daysOverdue = Math.abs(diffDays);
  }

  return {
    daysToClose,
    daysOverdue,
    urgency,
    bannerText,
    periodEndDate,
    isLoading: false,
  };
}

// ── Fallback — calendar month-end ─────────────────────────────────────────────

/**
 * Returns the last calendar day of the current month as an ISO 8601 date string.
 *
 * ⚠️  MOCK FALLBACK ONLY — see engineering note at the top of this file.
 * This is used when `fetchFinancePeriodEndDate()` returns null (i.e. the real
 * API has not yet been integrated). Do not use this as the permanent solution.
 */
function getCalendarMonthEnd(today: Date): string {
  // Day 0 of next month === last day of current month
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const yyyy = lastDay.getFullYear();
  const mm = String(lastDay.getMonth() + 1).padStart(2, '0');
  const dd = String(lastDay.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Resolves the effective period-end date.
 *
 * Priority:
 *   1. Real date from the finance system API (passed in as `apiDate`)
 *   2. ⚠️  Calendar month-end fallback (mock only — see engineering note)
 */
function resolveEffectivePeriodEnd(today: Date, apiDate: string | null): string {
  if (apiDate) return apiDate;
  // ⚠️  MOCK FALLBACK — Replace with real API date. See engineering note above.
  return getCalendarMonthEnd(today);
}

// ── Finance period-end API stub ───────────────────────────────────────────────

/**
 * Fetches the current period-end date from the connected finance system.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️  ENGINEERING TEAM — THIS FUNCTION NEEDS IMPLEMENTING
 * ─────────────────────────────────────────────────────────────────────────────
 * Currently returns null (triggers calendar month-end fallback).
 *
 * To implement:
 *   1. Call the Access Financials / Access Dimensions API to get the current
 *      open accounting period's end date for the given company/database.
 *   2. Return the period-end date as an ISO 8601 string, e.g. "2025-06-30".
 *   3. The `companyId` and `databaseId` parameters are provided so you can
 *      scope the request to the correct company's finance instance.
 *
 * Example signature once implemented:
 *   const response = await fetch(
 *     `${backendUrl}/api/companies/${companyId}/finance/current-period`,
 *     { credentials: 'include' }
 *   );
 *   const { periodEndDate } = await response.json();
 *   return periodEndDate; // e.g. "2025-06-30"
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * @param companyId  - The EBProjects company ID
 * @param databaseId - The finance system database/instance ID from the integration config
 * @returns ISO 8601 period-end date string, or null if unavailable
 */
async function fetchFinancePeriodEndDate(
  _companyId: number,
  _databaseId: string | null,
): Promise<string | null> {
  // ⚠️  NOT YET IMPLEMENTED — returns null to trigger calendar month-end fallback
  // See engineering note above for implementation instructions.
  return null;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

const NOT_CONNECTED_RESULT: MonthEndProximityResult = {
  daysToClose: null,
  daysOverdue: null,
  urgency: 'normal',
  bannerText: null,
  periodEndDate: null,
  isLoading: false,
};

/**
 * React hook that returns the month-end proximity result for the current company.
 *
 * - When finance is not connected, returns a safe 'normal' result with nulls.
 * - When connected, fetches the period-end date from the finance API (stub for
 *   now — falls back to calendar month-end until implemented).
 * - Re-fetches when the active company changes.
 *
 * Used by Finance dashboard widgets to apply conditional styling per PRD §2.9:
 *   - urgency === 'amber' → amber border + bannerText
 *   - urgency === 'red'   → red border + bannerText
 *   - urgency === 'normal' → no special treatment
 */
export function useMonthEndProximity(): MonthEndProximityResult {
  const { currentCompany } = useCompany();

  const [result, setResult] = useState<MonthEndProximityResult>({
    ...NOT_CONNECTED_RESULT,
    isLoading: true,
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!currentCompany) {
        if (!cancelled) setResult(NOT_CONNECTED_RESULT);
        return;
      }

      setResult(prev => ({ ...prev, isLoading: true }));

      try {
        // Attempt to fetch the real period-end date from the finance API.
        // ⚠️  fetchFinancePeriodEndDate() is a stub — returns null until implemented.
        // See engineering note at the top of this file.
        const apiDate = await fetchFinancePeriodEndDate(
          currentCompany.id,
          // databaseId will be on currentCompany once CompanyWithIntegrations is used here;
          // for now we pass null and let the fallback handle it.
          null,
        );

        if (cancelled) return;

        const today = new Date();
        const effectivePeriodEnd = resolveEffectivePeriodEnd(today, apiDate);
        const proximity = getMonthEndProximity(today, effectivePeriodEnd);

        setResult(proximity);
      } catch (err) {
        console.error('[useMonthEndProximity] Failed to fetch period-end date:', err);
        if (!cancelled) {
          // Fallback to calendar month-end on error — fail open, not closed
          const today = new Date();
          const fallback = getCalendarMonthEnd(today);
          setResult(getMonthEndProximity(today, fallback));
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [currentCompany]);

  return result;
}
